/**
 * An admin's options for one strip (ISSUE-1290), after v1's
 * strip_options_sheet.dart: Lock strip, Unlock strip and Copy members from a
 * strip, with the game as the subtitle.
 *
 * Lock asks once inside the sheet (it is not destructive: unlocking undoes
 * it). Unlock erases digits and winners, so it goes through the shared
 * `confirmDestructive`. Copy opens a second step listing the other strips.
 * The server draws the digits; this sheet only asks for it. Whether copying
 * is allowed is the server's `canCopyUsers`; the hint under it only explains
 * why not.
 */
import { Button, confirmDestructive, Dialog, Radio } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import type { typeStripView } from "./stripBoardModel";
import {
  STRIP_CANCEL_LABEL,
  STRIP_CLOSE_LABEL,
  STRIP_COPY_CONFIRM_LABEL,
  STRIP_COPY_FROM_LABEL,
  STRIP_COPY_USERS_DISABLED_CANCELED,
  STRIP_COPY_USERS_DISABLED_LOCKED,
  STRIP_COPY_USERS_DISABLED_ONE,
  STRIP_COPY_USERS_HINT,
  STRIP_COPY_USERS_LABEL,
  STRIP_LOCK_CONFIRM_BODY,
  STRIP_LOCK_HINT,
  STRIP_LOCK_LABEL,
  STRIP_UNLOCK_CONFIRM_LABEL,
  STRIP_UNLOCK_HINT,
  STRIP_UNLOCK_LABEL,
  stripCopyFromItemLabel,
  stripLockConfirmTitle,
  stripOptionsTitle,
  stripUnlockConfirmBody,
  stripUnlockConfirmTitle,
} from "./stripCardCopy";

type typeStep = "menu" | "lock" | "copy";

export type typeStripOptionsSheetProps = {
  open: boolean;
  strip: typeStripView | null;
  /** Every strip on the card, for the copy list. */
  strips: readonly typeStripView[];
  gameLabel: string;
  isCardCanceled: boolean;
  isBusy: boolean;
  onClose: () => void;
  onLock: (stripNumber: number) => Promise<boolean>;
  onUnlock: (stripNumber: number) => Promise<boolean>;
  onCopy: (input: {
    stripNumber: number;
    fromStripNumber: number;
  }) => Promise<boolean>;
};

function copyDisabledHint(input: {
  strip: typeStripView;
  stripCount: number;
  isCardCanceled: boolean;
}): string {
  if (input.isCardCanceled) return STRIP_COPY_USERS_DISABLED_CANCELED;
  if (input.strip.state === "locked") return STRIP_COPY_USERS_DISABLED_LOCKED;
  if (input.stripCount < 2) return STRIP_COPY_USERS_DISABLED_ONE;
  return STRIP_COPY_USERS_HINT;
}

export const StripOptionsSheet = (
  props: typeStripOptionsSheetProps,
): ReactNode => {
  const [step, setStep] = useState<typeStep>("menu");
  const [fromStripNumber, setFromStripNumber] = useState<string>("");
  const { strip } = props;

  useEffect(() => {
    if (!props.open) {
      setStep("menu");
      setFromStripNumber("");
    }
  }, [props.open]);

  if (strip === null) return null;
  const isLocked = strip.state === "locked";
  const others = props.strips.filter(
    (candidate) => candidate.stripNumber !== strip.stripNumber,
  );

  async function unlock(target: typeStripView): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: stripUnlockConfirmTitle(target.label),
      body: stripUnlockConfirmBody(target.label),
      confirmLabel: STRIP_UNLOCK_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;
    if (await props.onUnlock(target.stripNumber)) props.onClose();
  }

  const footer =
    step === "lock" ? (
      <>
        <Button
          variant="secondary"
          onClick={() => {
            setStep("menu");
          }}
        >
          {STRIP_CANCEL_LABEL}
        </Button>
        <Button
          variant="primary"
          data-testid="strip-lock-confirm"
          loading={props.isBusy}
          onClick={() => {
            void props.onLock(strip.stripNumber).then((isDone) => {
              if (isDone) props.onClose();
            });
          }}
        >
          {STRIP_LOCK_LABEL}
        </Button>
      </>
    ) : step === "copy" ? (
      <>
        <Button
          variant="secondary"
          onClick={() => {
            setStep("menu");
          }}
        >
          {STRIP_CANCEL_LABEL}
        </Button>
        <Button
          variant="primary"
          data-testid="strip-copy-confirm"
          disabled={fromStripNumber === ""}
          loading={props.isBusy}
          onClick={() => {
            void props
              .onCopy({
                stripNumber: strip.stripNumber,
                fromStripNumber: Number(fromStripNumber),
              })
              .then((isDone) => {
                if (isDone) props.onClose();
              });
          }}
        >
          {STRIP_COPY_CONFIRM_LABEL}
        </Button>
      </>
    ) : (
      <Button variant="secondary" onClick={props.onClose}>
        {STRIP_CLOSE_LABEL}
      </Button>
    );

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={
        step === "lock"
          ? stripLockConfirmTitle(strip.label)
          : stripOptionsTitle(strip.label)
      }
      description={props.gameLabel}
      autoFocus={false}
      footer={footer}
    >
      <div data-testid="strip-options-sheet" className="flex flex-col gap-3">
        {step === "lock" ? (
          <p className="text-body text-ink">{STRIP_LOCK_CONFIRM_BODY}</p>
        ) : step === "copy" ? (
          <Radio
            label={STRIP_COPY_FROM_LABEL}
            value={fromStripNumber}
            onValueChange={setFromStripNumber}
            items={others.map((candidate) => ({
              value: String(candidate.stripNumber),
              label: stripCopyFromItemLabel(candidate.label),
              description: `${String(candidate.filledCount)} of ${String(candidate.rows.length)} rows taken`,
            }))}
          />
        ) : (
          <>
            <OptionRow
              testId="strip-option-lock"
              label={STRIP_LOCK_LABEL}
              hint={STRIP_LOCK_HINT}
              disabled={isLocked || props.isCardCanceled}
              onClick={() => {
                setStep("lock");
              }}
            />
            <OptionRow
              testId="strip-option-unlock"
              label={STRIP_UNLOCK_LABEL}
              hint={STRIP_UNLOCK_HINT}
              disabled={!isLocked || props.isCardCanceled || props.isBusy}
              onClick={() => {
                void unlock(strip);
              }}
            />
            <OptionRow
              testId="strip-option-copy"
              label={STRIP_COPY_USERS_LABEL}
              hint={copyDisabledHint({
                strip,
                stripCount: props.strips.length,
                isCardCanceled: props.isCardCanceled,
              })}
              disabled={!strip.canCopyUsers}
              onClick={() => {
                setStep("copy");
              }}
            />
          </>
        )}
      </div>
    </Dialog>
  );
};

const OptionRow = (props: {
  testId: string;
  label: string;
  hint: string;
  disabled: boolean;
  onClick: () => void;
}): ReactNode => (
  <div className="flex flex-col gap-1">
    <Button
      variant="secondary"
      fullWidth
      data-testid={props.testId}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      {props.label}
    </Button>
    <p className="text-caption text-ink-secondary">{props.hint}</p>
  </div>
);
